import type { Db } from "./client";
import { throwIfPgError } from "./errors";
import type { GardenRow } from "@garden/types/db";

// Stage 08.5: a user can be a member of zero, one, or many gardens.
// `getGarden` returns the oldest one, used by callers that haven't yet
// migrated to a current-garden id. Prefer `getGardenById(client, id)` or
// `useCurrentGarden()` for new code.

/** First garden the user is a member of (oldest by created_at). Returns
 *  null when the user has no gardens yet (pre-bootstrap). */
export async function getGarden(client: Db): Promise<GardenRow | null> {
  const { data, error } = await client
    .from("garden")
    .select("*")
    .order("created_at", { ascending: true })
    .limit(1)
    .maybeSingle();
  throwIfPgError(error);
  return (data ?? null) as GardenRow | null;
}

/** Get a specific garden by id. RLS gates by membership; non-members
 *  get null. */
export async function getGardenById(
  client: Db,
  id: string,
): Promise<GardenRow | null> {
  const { data, error } = await client
    .from("garden")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  throwIfPgError(error);
  return (data ?? null) as GardenRow | null;
}

/** Bootstrap a garden for the calling user. Creates the garden row and
 *  immediately inserts a `garden_membership` row for the creator — a new
 *  garden with zero members would be invisible (RLS gates on
 *  is_garden_member). */
export async function getOrCreateGarden(
  client: Db,
  name = "My garden",
): Promise<GardenRow> {
  const existing = await getGarden(client);
  if (existing) return existing;
  return createGarden(client, name);
}

/** Create a new garden + add the creator as a member, atomically.
 *  Calls the `create_garden` SECURITY DEFINER RPC (migration 0013).
 *  Direct client-side insert+returning hits an RLS chicken-and-egg
 *  with the 0012 SELECT policy (`is_garden_member(id)` is false at
 *  RETURNING time because the membership row doesn't yet exist). */
export async function createGarden(
  client: Db,
  name: string,
): Promise<GardenRow> {
  const { data, error } = await client.rpc("create_garden", { p_name: name });
  throwIfPgError(error);
  if (!data) throw new Error("create_garden returned no row");
  return data as GardenRow;
}
